import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { Copy, CheckCircle, Server } from 'lucide-react';
import { useOgImage } from '@/hooks/useOgImage';

export const BackendDiagnostics = () => {
  const { toast } = useToast();
  const { fullOgImageUrl } = useOgImage();
  
  const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID || 'nicht gesetzt';
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'nicht gesetzt';
  
  const diagnosticData = [
    { label: 'Projekt-ID', value: projectId },
    { label: 'Supabase URL', value: supabaseUrl },
    { label: 'OG-Image URL', value: fullOgImageUrl },
  ];

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Kopiert!",
      description: `${label} wurde in die Zwischenablage kopiert.`,
    });
  };

  const copyAll = () => {
    const allData = diagnosticData
      .map(item => `${item.label}: ${item.value}`)
      .join('\n');
    navigator.clipboard.writeText(allData);
    toast({
      title: "Alle Daten kopiert!",
      description: "Diagnostik-Daten wurden in die Zwischenablage kopiert.",
    });
  };

  // Extract host from URL for quick visual check
  const getHostFromUrl = (url: string) => {
    try {
      return new URL(url).hostname;
    } catch {
      return url;
    }
  };

  return (
    <Card className="border-2 border-blue-200 bg-blue-50">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-lg text-blue-800">
          <Server className="w-5 h-5" />
          Backend-Diagnose
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {diagnosticData.map((item, index) => (
          <div key={index} className="flex flex-col gap-1">
            <span className="text-sm font-medium text-blue-700">{item.label}:</span>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs bg-white p-2 rounded border border-blue-200 break-all">
                {item.value}
              </code>
              <Button
                size="sm"
                variant="outline"
                onClick={() => copyToClipboard(item.value, item.label)}
                className="shrink-0"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
        
        {/* Quick visual check */}
        <div className="mt-4 p-3 bg-white rounded border border-blue-200">
          <div className="flex items-center gap-2 text-sm">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span className="font-medium">Backend-Host:</span>
            <code className="text-green-700 font-mono">
              {getHostFromUrl(supabaseUrl)}
            </code>
          </div>
        </div>

        <Button 
          onClick={copyAll} 
          className="w-full mt-4 bg-blue-600 hover:bg-blue-700"
        >
          <Copy className="w-4 h-4 mr-2" />
          Alle Daten kopieren
        </Button>
      </CardContent>
    </Card>
  );
};
